import { isMobileViewport } from '@utils/viewport';

/** Width of the open assistant panel, in px. */
export const ASSISTANT_PANEL_WIDTH = 400;

/** Width of the launcher rail shown when the panel is closed, in px. */
export const ASSISTANT_RAIL_WIDTH = 48;

const OPEN_STORAGE_KEY = 'proc-assistant-open';

type OpenMap = Record<string, boolean>;

const isOpenMap = (value: unknown): value is OpenMap =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every((open) => typeof open === 'boolean');

// Anything else in the slot is treated as absent rather than trusted:
// `JSON.parse('null')` is a valid parse whose indexing throws, which would
// take the whole editor down until the user cleared their storage by hand.
const readMap = (): OpenMap => {
  try {
    const raw = localStorage.getItem(OPEN_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return isOpenMap(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

/**
 * Whether the panel is open for this process.
 *
 * Open until the user collapses it: a right bar nobody notices is a feature
 * nobody uses, and the transcript it resolves on mount is bound to the process
 * and hidden from the main conversation list.
 */
export const readAssistantOpen = (processId: string): boolean =>
  readMap()[processId] ?? !isMobileViewport();

export const writeAssistantOpen = (processId: string, open: boolean): void => {
  try {
    localStorage.setItem(
      OPEN_STORAGE_KEY,
      JSON.stringify({ ...readMap(), [processId]: open }),
    );
  } catch {
    /* no-op — the panel still works, it just won't remember */
  }
};
